import type {
  AcceptedResponse,
  CreateRoleRequest,
  CreateUserRequest,
  CreateUserResponse,
  MessageResponse,
  RoleListResponse,
  RoleResponse,
  UpdateUserRolesRequest,
  UserListItemResponse,
  UserListParams,
  UserListResponse,
} from '@/shared/api/users';

const SYSTEM_ROLES = ['default-roles-dev-cyclone', 'offline_access', 'uma_authorization'] as const;

const initialRoles: RoleResponse[] = [
  { id: 'role-admin', name: 'admin', description: '전체 관리자 권한' },
  { id: 'role-manager', name: 'manager', description: '운영 관리자 권한' },
  { id: 'role-user', name: 'user', description: '일반 사용자 권한' },
];

const cloneUser = (user: UserListItemResponse): UserListItemResponse => ({
  ...user,
  roles: [...user.roles],
});

const cloneRole = (role: RoleResponse): RoleResponse => ({ ...role });

const normalizeSearchValue = (value?: string) => value?.trim().toLowerCase() ?? '';

const dedupeRoles = (roles: string[]) => Array.from(new Set(roles.filter(Boolean)));

const withSystemRoles = (roles: string[] = []) => dedupeRoles([...roles, ...SYSTEM_ROLES]);

const initialUsers: UserListItemResponse[] = [
  {
    id: 1,
    userId: 'user-management-yoonyoung-choi',
    username: '최윤영',
    email: 'yoonyoung.choi@onepredict.com',
    firstName: '윤영',
    lastName: '최',
    roles: withSystemRoles(['admin']),
  },
  {
    id: 2,
    userId: 'user-management-seoyeon-lee',
    username: '이서연',
    email: 'seoyeon.lee@onepredict.com',
    firstName: '서연',
    lastName: '이',
    roles: withSystemRoles(['manager']),
  },
  {
    id: 3,
    userId: 'user-management-jihoon-park',
    username: '박지훈',
    email: 'jihoon.park@onepredict.com',
    firstName: '지훈',
    lastName: '박',
    roles: withSystemRoles(['user']),
  },
  {
    id: 4,
    userId: 'user-management-yujin-choi',
    username: '최유진',
    email: 'yujin.choi@onepredict.com',
    firstName: '유진',
    lastName: '최',
    roles: withSystemRoles(['user']),
  },
  {
    id: 5,
    userId: 'user-management-daeun-jung',
    username: '정다은',
    email: 'daeun.jung@onepredict.com',
    firstName: '다은',
    lastName: '정',
    roles: withSystemRoles(['user']),
  },
];

const createInitialUsers = (): UserListItemResponse[] => initialUsers.map(cloneUser);

let users = createInitialUsers();
let roles = initialRoles.map(cloneRole);
let nextUserId = users.length + 1;

export const resetUserManagementMockStore = () => {
  users = createInitialUsers();
  roles = initialRoles.map(cloneRole);
  nextUserId = users.length + 1;
};

export const getUserManagementUsersMockData = (params: UserListParams = {}): UserListResponse => {
  const query = normalizeSearchValue(params.query);
  const currentPage = Math.max(1, Number(params.page) || 1);
  const pageSize = Math.max(1, Number(params.size) || 20);
  const filteredUsers = query
    ? users.filter((user) =>
        [user.username, user.email, user.firstName, user.lastName].some((field) => field.toLowerCase().includes(query))
      )
    : users;
  const startIndex = (currentPage - 1) * pageSize;
  const pagedUsers = filteredUsers.slice(startIndex, startIndex + pageSize).map(cloneUser);

  return {
    pagination: {
      currentPage,
      pageSize,
      totalCount: filteredUsers.length,
      totalPages: filteredUsers.length === 0 ? 0 : Math.ceil(filteredUsers.length / pageSize),
    },
    users: pagedUsers,
  };
};

export const getUserManagementRolesMockData = (): RoleListResponse => ({
  roles: roles.map(cloneRole),
});

export const createUserManagementUserMockData = (request: CreateUserRequest): CreateUserResponse => {
  const username = request.username.trim();
  const id = nextUserId;
  const userId = `user-management-${String(id).padStart(3, '0')}`;

  users = [
    ...users,
    {
      id,
      userId,
      username,
      email: request.email?.trim() || `${username}@onepredict.com`,
      firstName: request.firstName?.trim() || username,
      lastName: request.lastName?.trim() || '',
      roles: withSystemRoles(request.realmRoles ?? []),
    },
  ];
  nextUserId += 1;

  return { userId, username };
};

export const createUserManagementRoleMockData = (request: CreateRoleRequest): RoleResponse => {
  const name = request.name.trim();
  const role: RoleResponse = {
    id: `role-${name.toLowerCase().replace(/[^a-z0-9_-]+/g, '-')}`,
    name,
    description: request.description?.trim() || null,
  };

  roles = [...roles, role];

  return cloneRole(role);
};

export const updateUserManagementUserRolesMockData = (id: number, request: UpdateUserRolesRequest): MessageResponse => {
  const userIndex = users.findIndex((user) => user.id === id);

  if (userIndex < 0) {
    throw new Error(`User not found: ${id}`);
  }

  users = users.map((user, index) =>
    index === userIndex
      ? {
          ...user,
          roles: withSystemRoles(request.roleNames),
        }
      : cloneUser(user)
  );

  return { message: 'User roles updated' };
};

export const deleteUserManagementUserMockData = (id: number): AcceptedResponse => {
  const userExists = users.some((user) => user.id === id);

  if (!userExists) {
    throw new Error(`User not found: ${id}`);
  }

  users = users.filter((user) => user.id !== id).map(cloneUser);

  return { message: 'User deleted' };
};
